/**
 * TermsOfService.js - the Constellation KG Terms of Service page.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MarketingFooter from '../common/MarketingFooter/MarketingFooter';
import './TermsOfService.css';
import { DOCUMENT_VERSIONS, formatDocumentDate } from '../../constants/documentVersions';

const TermsOfService = () => {
  const navigate = useNavigate();

  // Handle anchor link clicks with smooth scrolling
  const handleAnchorClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${targetId}`);
    }
  };

  return (
    <div className="terms-of-service">
      <div className="terms-container">
        {/* Back Navigation */}
        <button 
          className="back-button"
          onClick={() => navigate('/')}
          aria-label="Back to home"
        >
          <ArrowLeft size={20} />
          Back to Home
        </button>

        {/* Header */}
        <header className="terms-header">
          <h1>
            <button 
              onClick={() => navigate('/')} 
              className="header-link"
              aria-label="Return to Constellation KG home"
            >
              Constellation KG
            </button>
            {' Terms of Service'}
          </h1>
          <p className="terms-meta">
            <strong>Last Updated:</strong> {formatDocumentDate(DOCUMENT_VERSIONS.tos)}<br />
            <strong>Effective Date:</strong> November 19, 2025<br />
            <strong>Status:</strong> Alpha Testing Agreement
          </p>
        </header>

        {/* Welcome Section */}
        <section className="terms-section">
          <h2>Terms of Service for Constellation KG's Invitation-Only Alpha Test</h2>
          <p>
            Thank you for your interest in Constellation KG! We are just beginning a closed, invitation-only alpha test. 
            We appreciate your insights into how you use and might use the system, and we look forward to more of you 
            joining us as we progress to beta testing and, ultimately, into production.
          </p>
          
          <div className="key-points">
            <p><strong>What You're Agreeing To:</strong></p>
            <ul>
              <li>Test experimental software that may break, crash, or lose data</li>
              <li>Provide feedback to help us improve the platform</li>
              <li>Accept that service may be unavailable without notice</li>
              <li>Understand that features will change as we learn, iterate, and innovate</li>
              <li>Use CKG at your own risk during this testing phase</li>
            </ul>
          </div>

          <div className="info-box">
            <p><strong>Our Commitment to You:</strong></p>
            <p>
              We will do our best to preserve your data and maintain service availability, but we cannot guarantee either 
              during alpha testing or as we transition from alpha testing to beta testing and, ultimately, production. We 
              know that knowledge work has massive sunk costs and we will do our best to honor the time, energy, and thought 
              you put into the system. We will be transparent about issues, responsive to feedback, and honest about what's 
              working and what isn't. Your trust and patience during this phase are deeply appreciated.
            </p>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="table-of-contents">
          <h2>Table of Contents</h2>
          <ol>
            <li><a href="#section-1" onClick={(e) => handleAnchorClick(e, 'section-1')}>Acceptance of Terms</a></li>
            <li><a href="#section-2" onClick={(e) => handleAnchorClick(e, 'section-2')}>Alpha Testing Acknowledgment</a></li>
            <li><a href="#section-3" onClick={(e) => handleAnchorClick(e, 'section-3')}>Account Registration & Access</a></li>
            <li><a href="#section-4" onClick={(e) => handleAnchorClick(e, 'section-4')}>Permitted Use & Restrictions</a></li>
            <li><a href="#section-5" onClick={(e) => handleAnchorClick(e, 'section-5')}>Service Availability & Performance</a></li>
            <li><a href="#section-6" onClick={(e) => handleAnchorClick(e, 'section-6')}>Your Data & Intellectual Property</a></li>
            <li><a href="#section-7" onClick={(e) => handleAnchorClick(e, 'section-7')}>Data Preservation & Migration</a></li>
            <li><a href="#section-8" onClick={(e) => handleAnchorClick(e, 'section-8')}>Feedback & Bug Reports</a></li>
            <li><a href="#section-9" onClick={(e) => handleAnchorClick(e, 'section-9')}>Disclaimers & No Warranties</a></li>
            <li><a href="#section-10" onClick={(e) => handleAnchorClick(e, 'section-10')}>Limitation of Liability</a></li>
            <li><a href="#section-11" onClick={(e) => handleAnchorClick(e, 'section-11')}>Termination</a></li>
            <li><a href="#section-12" onClick={(e) => handleAnchorClick(e, 'section-12')}>Changes to Terms</a></li>
            <li><a href="#section-13" onClick={(e) => handleAnchorClick(e, 'section-13')}>General Legal Terms</a></li>
            <li><a href="#section-14" onClick={(e) => handleAnchorClick(e, 'section-14')}>Contact Information</a></li>
          </ol>
        </nav>

        {/* Section 1 */}
        <section id="section-1" className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          
          <p>
            By creating an account, accessing, or using Constellation KG, you agree to be bound by these Terms of Service and 
            our <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy Policy</a>.
          </p>

          <p>
            <strong>If you do not agree to these terms, you may not use CKG.</strong>
          </p>

          <h3>1.1 Who May Use CKG</h3>
          <ul>
            <li>You must be at least 18 years old to create an account.</li>
            <li>You must be invited to participate in alpha testing</li>
            <li>You must have authority to agree to these terms</li>
            <li>Your use must comply with all applicable laws in your jurisdiction</li>
          </ul>

          <h3>1.2 About Constellation KG</h3>
          <p>
            Constellation KG is operated by Constellation KG LLC, a Connecticut limited liability company. These terms constitute 
            a legal agreement between you and Constellation KG LLC.
          </p>
        </section>

        {/* Section 2 */}
        <section id="section-2" className="terms-section">
          <h2>2. Alpha Testing Acknowledgment</h2>

          <div className="warning-box">
            <p><strong>⚠️ This is experimental software under active development.</strong></p>
          </div>

          <p>
            By participating in alpha testing, you explicitly acknowledge and accept that:
          </p>

          <h3>2.1 Experimental Nature</h3>
          <ul>
            <li><strong>Incomplete Features:</strong> Many features are partially implemented or missing entirely</li>
            <li><strong>Bugs & Errors:</strong> The software contains known and unknown bugs that may cause unexpected behavior</li>
            <li><strong>Performance Issues:</strong> The platform has not been stress-tested and may perform poorly under load</li>
            <li><strong>Breaking Changes:</strong> Features may be added, modified, or removed without notice</li>
            <li><strong>Data Integrity:</strong> While we intend to preserve your data, we cannot guarantee against data loss or corruption</li>
          </ul>

          <h3>2.2 No Production Readiness</h3>
          <p className="emphasis-text">
            <strong>CKG is NOT production-ready software.</strong> Do not rely on it for critical information or workflows where 
            data loss would be unacceptable.
          </p>

          <h3>2.4 Testing Duration</h3>
          <p>
            The alpha testing phase duration will depend on testing results and
            development progress. We will notify all testers in advance of any
            transition to beta testing.
          </p>
        </section>

        {/* Section 3 */}
        <section id="section-3" className="terms-section">
          <h2>3. Account Registration & Access</h2>

          <h3>3.1 Invitation-Only Access</h3>
          <p>
            Alpha testing is by invitation only. You must have received an
            invitation to create an account. When approved, you will receive a
            one-time welcome link by email that expires after 48 hours.
          </p>

          <h3>3.2 Account Information</h3>
          <p>When creating an account, you agree to:</p>
          <ul>
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your password</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>

          <h3>3.3 One Account Per Person</h3>
          <p>
            Each person may have only one CKG account during alpha testing. Creating multiple accounts may result in termination 
            of all your accounts.
          </p>

          <h3>3.4 Account Security</h3>
          <p>
            Your account is protected by:
          </p>
          <ul>
            <li>Bcrypt password hashing (10 rounds)</li>
            <li>Account lockout after 5 failed login attempts (15-minute cooldown)</li>
            <li>HTTP-only, secure session cookies</li>
            <li>Access tokens expire 10 hours after they are issued; refresh
            tokens expire after 30 days. The access-token cookie is removed when
            you close your browser and the refresh-token cookie after 7 days</li>
          </ul>
          <p>
            However, during alpha testing, security features may have vulnerabilities. If you discover a security issue, please 
            report it immediately to <a href="mailto:info@constellationkg.com">info@constellationkg.com</a>.
          </p>
        </section>

        {/* Section 4 */}
        <section id="section-4" className="terms-section">
          <h2>4. Permitted Use & Restrictions</h2>

          <h3>4.1 Permitted Uses</h3>
          <p>You may use CKG to:</p>
          <ul className="privacy-features">
            <li>Capture and organize knowledge from web-based sources</li>
            <li>Create Focuses (passages of text) and Thoughts (your reflections)</li>
            <li>Create Journal Entries</li>
            <li>Tag and categorize your Knowledge Entries</li>
            <li>Search and explore your knowledge base</li>
            <li>Follow Unexplored Links</li>
            <li>Claim entries from Collective Intelligence into your own knowledge base</li>
            <li>Use AI Explorer to organize and describe your own entries in relation to a question you ask (optional).</li>
            <li>Provide feedback about your testing experience</li>
          </ul>
          <p>
            AI Explorer uses a third-party AI to thematically organize your own work. Every citation in an analysis is
            traceable to its source. Neither CKG nor its AI providers recommend or discourage any choice reflected in that work.
          </p>

          <h3>4.2 Prohibited Uses</h3>
          <p>You may NOT use CKG to:</p>
          <ul className="not-collected">
            <li>Upload content you don't have rights to share (though CKG is designed for personal knowledge management and fair use)</li>
            <li>Use CKG to systematically copy entire copyrighted works</li>
            <li>Store content that violates others' privacy or legal rights</li>
            <li>Attempt to hack, reverse engineer, or compromise the system</li>
            <li>Use automated tools (bots, scrapers) without explicit permission</li>
            <li>Attempt to access other users' accounts or data</li>
            <li>Share your account credentials or login links with others</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Overload the system intentionally (e.g., stress testing without permission)</li>
            <li>Resell CKG or provide access to it as a service</li>
            <li>Use CKG to build a competing product</li>
            <li>Use CKG's AI features in ways that violate the usage policies of our AI providers: Anthropic's <a
            href="https://www.anthropic.com/legal/aup" target="_blank" rel="noopener noreferrer">Usage Policy</a> and
            OpenAI's <a href="https://openai.com/policies/usage-policies/" target="_blank"
            rel="noopener noreferrer">Usage Policies</a>.</li>
          </ul>

          <h3>4.3 Content Responsibility</h3>
          <p>
            You are solely responsible for all content you create, store, or transmit through CKG. As described in our Privacy 
            Policy, Focuses and document metadata are shared with other users through Collective Intelligence; your
            Thoughts and your Tags are never shown to anyone.
          </p>

          <h3>4.4 Respect for Source Material</h3>
          <p>
            When capturing Focuses from web content, you should:
          </p>
          <ul>
            <li>Respect copyright and fair use principles</li>
            <li>Not reproduce entire copyrighted works</li>
            <li>Be mindful that you're capturing others' intellectual property for personal study</li>
          </ul>
          <p>
            CKG is designed for personal knowledge management within the bounds of fair use. Your use of passages for personal 
            study and reflection is consistent with fair use doctrine.
          </p>
        </section>

        {/* Section 5 */}
        <section id="section-5" className="terms-section">
          <h2>5. Service Availability & Performance</h2>

          <h3>5.1 No Uptime Guarantees</h3>
          <div className="warning-box">
            <p>
              <strong>CKG may go down or be unavailable at any time without notice.</strong> We do not guarantee any level of 
              service availability during alpha testing.
            </p>
          </div>

          <h3>5.2 Expected Issues</h3>
          <p>During alpha testing, you should expect:</p>
          <ul>
            <li><strong>Unplanned Outages:</strong> The service may become unavailable without warning</li>
            <li><strong>Slow Performance:</strong> Response times may vary significantly</li>
            <li><strong>Concurrent User Issues:</strong> The system has not been tested with multiple simultaneous users</li>
            <li><strong>Browser Extension Problems:</strong> The extension may fail to capture content correctly</li>
            <li><strong>Search Inconsistencies:</strong> Search results may be incomplete or inaccurate</li>
            <li><strong>AI Explorer Failures:</strong> Third-party AI services may be unavailable or return errors</li>
          </ul>

          <h3>5.3 Maintenance & Updates</h3>
          <p>
            We may perform maintenance, deploy updates, or make changes to CKG at any time. When possible, we will provide 
            advance notice, but this is not guaranteed during alpha testing.
          </p>

          <h3>5.4 Browser Compatibility</h3>
          <p>
            CKG is currently optimized for Chrome/Chromium browsers. The browser extension is Chrome-only at this time. We plan 
            to expand browser support in future releases, but alpha testing is Chrome-focused. The web application may work in 
            Firefox, Safari, and Edge, but is not officially tested or supported on these browsers during alpha.
          </p>
        </section>

        {/* Section 6 - COMPLETELY REVISED TO ALIGN WITH PRIVACY POLICY */}
        <section id="section-6" className="terms-section">
          <h2>6. Your Data & Intellectual Property</h2>

          <h3>6.1 Understanding CKG's Two-Layer Architecture</h3>
          <p>
            As detailed in our <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy Policy</a>, 
            CKG uses two layers for your data:
          </p>

          <div className="highlight-box">
            <p><strong>Your Thoughts are private and under your control.</strong></p>
          </div>

          <p><strong>The Private Layer - Your Thoughts:</strong></p>
          <ul className="privacy-features">
            <li>Your Thoughts are yours alone</li>
            <li>Never shared with other users</li>
            <li>Never used as a system resource</li>
            <li>Only accessed in user-specific contexts (your searches, your AI queries)</li>
            <li>Never aggregated or analyzed system-wide</li>
            <li>Protected by architectural design</li>
            <li>A Journal Entry is a Thought without a Focus — your own writing, private in exactly the same way.</li>
          </ul>

          <p><strong>The Shared Layer - Focuses & Document Metadata:</strong></p>
          <ul>
            <li>Focuses and document metadata are shared with other users through Collective Intelligence; your Thoughts
            and your Tags are never shown to anyone.</li>
            <li>Document metadata (title, URL, publication date) is already public information.</li>
            <li>Nothing in Collective Intelligence identifies you — not your name, not an account identifier, not a
            pseudonym.</li>
          </ul>

          <h3>6.2 What You Grant to CKG</h3>
          <p>
            By using CKG, you grant Constellation KG LLC the right to:
          </p>
          <ul>
            <li><strong>Store and process all your content</strong> (Thoughts, Focuses, Tags, metadata) to provide the service</li>
            <li><strong>Use Focuses and document metadata as shared assets</strong> for
CKG's Collective Intelligence — anonymized and never linked to
your identity</li>
            <li><strong>Access your Thoughts only in user-specific contexts:</strong> your searches, your AI Explorer queries, and other features you
explicitly use</li>
          </ul>

          <p><strong>What CKG Will NOT Do:</strong></p>
          <ul className="not-collected">
            <li>Share your Thoughts with anyone</li>
            <li>Use your Thoughts for system-wide analysis or aggregation</li>
            <li>Associate your Focuses or Tags with your real identity</li>
            <li>Sell or monetize your personal data</li>
          </ul>

          <h3>6.3 When You Delete Content</h3>
          <p>
            <strong>If you delete a Thought:</strong> the Thought, its embedding, and its sentiment are removed. AI Explorer
            analyses you ran before the deletion are your own record and are kept; the deleted Thought is not used in any
            future analysis.
          </p>

          <p>
            <strong>If you delete a Knowledge Entry:</strong> your Thought, your Tags, and your association are deleted; the
            Focus and metadata remain as shared assets but are no longer associated with you.
          </p>

          <p>
            <strong>If you delete a Journal Entry:</strong> it is removed along with its embedding and sentiment, as a
            Thought is.
          </p>

          <h3>6.4 AI Explorer Data Sharing</h3>
          <p>
            When you use AI Explorer, you choose to send a subset of your knowledge base (including Focuses, Thoughts, and Journal Entries) to
            a third-party AI service. This is detailed in Section 5 of our Privacy Policy. You control when and how this happens.
          </p>

          <h3>6.5 CKG Intellectual Property</h3>
          <p>
            The CKG platform, including its code, design, features, and trademarks, remains the property of Constellation KG LLC. 
            You may not copy, modify, or create derivative works based on CKG without permission.
          </p>

          <h3>6.6 Feedback License</h3>
          <p>
            Any feedback, suggestions, or ideas you provide about CKG may be used by us without obligation or compensation to you. 
            If you want to keep ideas confidential, do not share them with us.
          </p>
        </section>

        {/* Section 7 */}
        <section id="section-7" className="terms-section">
          <h2>7. Data Preservation & Migration</h2>

          <h3>7.1 Our Intent</h3>
          <p>
            We intend to preserve your data throughout alpha testing and carry it forward to beta testing. This is our goal and 
            we will make reasonable efforts to achieve it.
          </p>

          <h3>7.2 No Guarantees</h3>
          <div className="warning-box">
            <p>
              <strong>However, we cannot guarantee data preservation during alpha testing.</strong> Data loss may occur due to 
              bugs, infrastructure issues, or the need to reset the database during development.
            </p>
          </div>

          <h3>7.3 Data Export</h3>
          <p>
            Data export is available now. From your account page you can
            download your Knowledge Entries (including your Thoughts) and
            Journal Entries in JSON or CSV format at any time.
          </p>
          <p>
            AI Explorer analyses can be downloaded as PDFs when you run a
            query. Account deletion and full data erasure are still in
            development — see Section 11 for details.
          </p>

          <h3>7.4 Database Resets</h3>
          <p>
            If a database reset becomes necessary due to structural changes or critical bugs, we will:
          </p>
          <ul>
            <li>Provide as much advance notice as possible (target: 7 days)</li>
            <li>Explain why the reset is necessary</li>
            <li>Offer data export if functionality is available</li>
            <li>Help you preserve important content where feasible</li>
          </ul>
        </section>

        {/* Section 8 */}
        <section id="section-8" className="terms-section">
          <h2>8. Feedback & Bug Reports</h2>

          <h3>8.1 Your Feedback is Valuable</h3>
          <p>
            As an alpha tester, your feedback directly shapes CKG's development. We encourage you to report:
          </p>
          <ul>
            <li><strong>Bugs:</strong> Anything that breaks, errors, or behaves unexpectedly</li>
            <li><strong>Usability Issues:</strong> Features that are confusing or frustrating</li>
            <li><strong>Feature Requests:</strong> Ideas for improvements or new capabilities</li>
            <li><strong>Performance Problems:</strong> Slow loads, timeouts, or crashes</li>
          </ul>

          <h3>8.2 How to Provide Feedback</h3>
          <p>
            Email: <a href="mailto:info@constellationkg.com">info@constellationkg.com</a>
          </p>

          <h3>8.3 No Obligation to Implement</h3>
          <p>
            While we value your feedback, we are not obligated to implement any suggestions or fix all reported bugs during alpha 
            testing. Development priorities may shift based on overall testing results.
          </p>

          <h3>8.4 Testing Communication</h3>
          <p>
            We may contact you via email about:
          </p>
          <ul>
            <li>Critical bugs or security issues</li>
            <li>Major changes to the service</li>
            <li>Requests for specific testing or feedback</li>
            <li>Transition to beta testing</li>
          </ul>
        </section>

        {/* Section 9 */}
        <section id="section-9" className="terms-section">
          <h2>9. Disclaimers & No Warranties</h2>

          <div className="warning-box">
            <p>
              <strong>PLEASE READ THIS SECTION CAREFULLY.</strong> It limits our liability during alpha testing.
            </p>
          </div>

          <h3>9.1 "AS IS" Service</h3>
          <p>
            CKG is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, either express or implied, 
            including but not limited to:
          </p>
          <ul className="not-collected">
            <li>Warranties of merchantability</li>
            <li>Fitness for a particular purpose</li>
            <li>Non-infringement</li>
            <li>Security or accuracy</li>
            <li>Uninterrupted or error-free operation</li>
            <li>Data preservation or backup</li>
          </ul>

          <h3>9.2 No Guarantee of Functionality</h3>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>CKG will meet your requirements</li>
            <li>The service will be uninterrupted, timely, secure, or error-free</li>
            <li>Results obtained from CKG will be accurate or reliable</li>
            <li>Any errors will be corrected</li>
            <li>CKG is free of viruses or harmful components</li>
          </ul>

          <h3>9.3 Third-Party Services</h3>
          <p>
            CKG integrates with the following third-party services: Anthropic
            (Claude), the only provider that generates analysis; OpenAI, used
            for embeddings and sentiment analysis only; and Resend for
            transactional email delivery. CKG's sentiment analysis uses an
            OpenAI chat model, but it is not used to answer questions or
            generate text. It returns a short structured result, and CKG stores
            only a label. We are not responsible for the
            availability, accuracy, or content of these third-party services.
            Full details of what each provider receives are in Section 5 of
            our Privacy Policy.
          </p>

          <h3>9.4 Your Risk</h3>
          <p>
            You use CKG at your own risk. You are solely responsible for any damage to your computer system, loss of data, or 
            other harm that results from accessing or using CKG.
          </p>
        </section>

        {/* Section 10 */}
        <section id="section-10" className="terms-section">
          <h2>10. Limitation of Liability</h2>

          <div className="warning-box">
            <p>
              <strong>IMPORTANT:</strong> This section limits how much we can be held liable for if something goes wrong.
            </p>
          </div>

          <h3>10.1 No Liability for Damages</h3>
          <p>
            To the maximum extent permitted by law, Constellation KG LLC, its officers, directors, employees, and agents shall 
            not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
          </p>
          <ul>
            <li>Loss of data or content</li>
            <li>Loss of profits or revenue</li>
            <li>Loss of business opportunity</li>
            <li>Business interruption</li>
            <li>Loss of goodwill</li>
            <li>Emotional distress</li>
          </ul>

          <p>This applies whether the claim is based on warranty, contract, tort, or any other legal theory.</p>

          <h3>10.2 Maximum Liability</h3>
          <p>
            Our total liability to you for all claims arising from or related to CKG shall not exceed the greater of:
          </p>
          <ul>
            <li>$100 (one hundred dollars), or</li>
            <li>The amount you paid to use CKG in the 12 months before the claim arose</li>
          </ul>

          <p>
            Since alpha testing is currently free, this means our maximum liability is $100.
          </p>

          <h3>10.3 Basis of the Bargain</h3>
          <p>
            These limitations reflect the allocation of risk between us and you. The limitations in this section will apply even 
            if any remedy fails of its essential purpose.
          </p>

          <h3>10.4 Exceptions</h3>
          <p>
            Some jurisdictions do not allow certain liability limitations. In such cases, our liability is limited to the maximum 
            extent permitted by law.
          </p>
        </section>

        {/* Section 11 */}
        <section id="section-11" className="terms-section">
          <h2>11. Termination</h2>

          <h3>11.1 Your Right to Terminate</h3>
          <p>
            You may stop using CKG at any time. To fully terminate your account:
          </p>
          <ul>
            <li>Contact us at <a href="mailto:info@constellationkg.com">info@constellationkg.com</a> to request account deletion</li>
            <li>Account deletion functionality is currently in development</li>
            <li>We will delete your account and data within 30 days of your request, in accordance with our Privacy Policy and 
            applicable data retention requirements</li>
          </ul>

          <h3>11.2 Our Right to Terminate</h3>
          <p>
            We may suspend or terminate your access to CKG at any time, with or without cause, including if:
          </p>
          <ul>
            <li>You violate these Terms of Service</li>
            <li>You engage in prohibited activities</li>
            <li>We decide to end the alpha testing program</li>
            <li>Technical or legal issues require it</li>
            <li>We determine it necessary for security or operational reasons</li>
          </ul>

          <h3>11.3 Effect of Termination</h3>
          <p>
            Upon termination:
          </p>
          <ul>
            <li>Your right to access CKG immediately ceases</li>
            <li>We may delete your account and data (subject to our data retention obligations under the Privacy Policy)</li>
            <li>Sections that by their nature should survive termination will continue to apply (including disclaimers, limitations 
            of liability, and intellectual property provisions)</li>
          </ul>

          <h3>11.4 End of Alpha Testing</h3>
          <p>
            When alpha testing concludes, we will:
          </p>
          <ul>
            <li>Notify all testers at least 14 days in advance</li>
            <li>Explain the transition to beta testing</li>
            <li>Offer to preserve your data for beta (if you choose to continue)</li>
            <li>Provide data export options if available</li>
          </ul>
        </section>

        {/* Section 12 */}
        <section id="section-12" className="terms-section">
          <h2>12. Changes to Terms</h2>

          <h3>12.1 Right to Modify</h3>
          <p>
            We may modify these Terms of Service at any time during alpha testing as the platform evolves. Changes may be 
            necessary to reflect new features, address issues, or comply with legal requirements.
          </p>

          <h3>12.2 Notice of Changes</h3>
          <p>
            CKG is in active development and these terms will evolve as the
            platform does. When we make material changes, we will update the
            ‘Last Updated’ date at the top of these terms, ask you to accept the
            revised terms the next time you sign in, and email you about major
            changes as described in Section 8.4.
          </p>

          <h3>12.3 Acceptance of Changes</h3>
          <p>
            When these terms change, you will be asked to accept the revised terms before continuing to use CKG. If you do not
            agree, you may export your data and close your account as described in Section 11.
          </p>

          <h3>12.4 Transition to Production</h3>
          <p>
            When CKG moves from alpha to beta or from beta to production, we will provide entirely new Terms of Service appropriate 
            to that phase. Alpha testing terms will not automatically carry forward.
          </p>
        </section>

        {/* Section 13 */}
        <section id="section-13" className="terms-section">
          <h2>13. General Legal Terms</h2>

          <h3>13.1 Governing Law</h3>
          <p>
            These Terms of Service are governed by and construed in accordance with the laws of the State of Connecticut, United 
            States, without regard to its conflict of law principles.
          </p>

          <h3>13.2 Dispute Resolution</h3>
          <p>
            Any disputes arising from these terms or your use of CKG shall be resolved in the state or federal courts located in 
            Connecticut. You consent to the personal jurisdiction of these courts.
          </p>

          <h3>13.3 Entire Agreement</h3>
          <p>
            These Terms of Service, together with our Privacy Policy, constitute the entire agreement between you and Constellation 
            KG LLC regarding CKG and supersede any prior agreements.
          </p>

          <h3>13.4 Severability</h3>
          <p>
            If any provision of these terms is found to be unenforceable or invalid, that provision will be limited or eliminated 
            to the minimum extent necessary, and the remaining provisions will remain in full effect.
          </p>

          <h3>13.5 No Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these terms will not be deemed a waiver of such right or provision.
          </p>

          <h3>13.6 Assignment</h3>
          <p>
            You may not assign or transfer these terms or your account to anyone else. We may assign our rights and obligations 
            under these terms without restriction.
          </p>

          <h3>13.7 Force Majeure</h3>
          <p>
            We are not liable for any failure or delay in performance due to circumstances beyond our reasonable control, including 
            but not limited to acts of God, natural disasters, war, terrorism, riots, labor disputes, or internet/network failures.
          </p>

          <h3>13.8 Export Compliance</h3>
          <p>
            You agree to comply with all applicable export and import control laws and regulations in your use of CKG.
          </p>
        </section>

        {/* Section 14 */}
        <section id="section-14" className="terms-section">
          <h2>14. Contact Information</h2>
          
          <p><strong>Constellation KG LLC</strong></p>
          <p>
            <strong>Email:</strong> <a href="mailto:info@constellationkg.com">info@constellationkg.com</a><br />
            <strong>Response Time:</strong> Within 48 hours for initial response
          </p>

          <p>
            For all inquiries including general questions, legal matters, privacy requests, security issues, bug reports, and 
            feedback, please contact us at the email address above.
          </p>

          <p>
            For legal notices and official correspondence, please include "LEGAL NOTICE" in the subject line.
          </p>
        </section>

        {/* Alpha Tester Section */}
        <section className="alpha-section">
          <h2>A Note to Our Early Adopters</h2>

          <p>
            First, foremost, and above all things: thank you! We know how
            precious your time is and any time you give us in testing
            Constellation KG and offering feedback on it is deeply, deeply
            appreciated.
          </p>

          <p>
            These terms may seem formal for a new product that is not yet
            formally in production, but they're necessary to protect both
            of us as we explore together what CKG can become.
          </p>

          <p>
            The legal language above doesn't change the fundamental
            relationship: you're testing a new way for us, as people, to
            use and interact with artificial intelligence. We're incredibly
            grateful for your patience and feedback. If anything in these
            terms concerns you or seems unclear, please reach out. We're
            building this together.
          </p>

          <h3>What We're Really Asking</h3>
          <ul>
            <li><strong>Be patient</strong> when things break (they will)</li>
            <li><strong>Share feedback</strong> honestly about what works
            and what doesn't</li>
            <li><strong>Don't rely on CKG</strong> for anything critical
            (yet) — play with it. Test it. Have fun with it.</li>
            <li><strong>Understand</strong> that this is a learning process
            for all of us</li>
            <li><strong>Know</strong> that we're doing our best to build
            something that helps you better manage and interact with the
            information you find interesting and important.</li>
          </ul>

          <p className="thank-you">
            Again, thank you for being part of this journey. Your
            participation shapes the future of Constellation KG.
          </p>
        </section>

        {/* Back to Top Button */}
        <div className="back-to-top-container">
          <button 
            className="back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Back to Top ↑
          </button>
        </div>
      </div>

      {/* Shared Marketing Footer */}
      <MarketingFooter />
    </div>
  );
};

export default TermsOfService;
